import { useCallback, useState } from "react"

import { aiGenerationCreditCosts } from "@forento/core/models/ai"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

type Args = {
	isEnabled: boolean
	hasUnsavedChanges: boolean
	onApplied(): Promise<void>
}

export type WebsiteAiEdit = ReturnType<typeof useWebsiteAiEdit>

/** The website chat's state, shared by the chat panel and the preview that renders its proposal. */
export default function useWebsiteAiEdit({ isEnabled, hasUnsavedChanges, onApplied }: Args) {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const utilities = query.useUtils()

	const [message, setMessage] = useState("")
	const [dismissedPreviewTurnId, setDismissedPreviewTurnId] = useState<number | null>(null)

	// Edits normally complete within the mutation; a pending turn only appears here when an earlier
	// request was interrupted, so the poll exists to pick up how that turn ended.
	const edits = query.ai.listWebsiteEdits.useQuery(undefined, {
		enabled: isEnabled,
		refetchInterval: q =>
			q.state.data?.some(turn => turn.status === "queued" || turn.status === "generating") ? 2000 : false,
	})

	const pendingTurnId = edits.data?.find(turn => turn.status === "queued" || turn.status === "generating")?.id ?? null
	const undecidedTurn =
		edits.data?.find(
			turn =>
				turn.status === "completed" &&
				turn.changes !== null &&
				turn.changes.length > 0 &&
				turn.decision === null,
		) ?? null

	const previewQuery = query.ai.getWebsiteEditPreview.useQuery(undecidedTurn?.id ?? 0, {
		enabled: isEnabled && undecidedTurn !== null,
	})
	const preview = undecidedTurn !== null ? (previewQuery.data ?? null) : null

	const sendEdit = query.ai.editWebsite.useMutation({
		async onError(error, variables) {
			console.error(error)
			// Sending clears the input, so a failed message goes back into it to retry or edit.
			setMessage(variables.message)
			await Promise.all([edits.refetch(), user.reload()])
			await alert.show(
				"Failed to send edit request",
				error.message || "An error occurred while sending your edit request. Please try again.",
			)
		},
		async onSuccess() {
			await Promise.all([edits.refetch(), user.reload()])
		},
	})

	const applyEdit = query.ai.applyWebsiteEdit.useMutation({
		async onError(error) {
			console.error(error)
			await edits.refetch()
			await alert.show(
				"Failed to apply changes",
				error.message || "An error occurred while applying the changes. Please try again.",
			)
		},
		async onSuccess(data) {
			await Promise.all([onApplied(), edits.refetch()])
			if (data.skippedOperationCount > 0) {
				await alert.show(
					"Some changes were skipped",
					"Part of your website changed after these edits were proposed, so some of them no longer applied.",
				)
			}
		},
	})

	const discardEdit = query.ai.discardWebsiteEdit.useMutation({
		async onError(error) {
			console.error(error)
			await edits.refetch()
			await alert.show(
				"Failed to discard changes",
				error.message || "An error occurred while discarding the changes. Please try again.",
			)
		},
		async onSuccess() {
			await edits.refetch()
		},
	})

	const isGenerating = sendEdit.isPending || pendingTurnId !== null
	const editProgress = query.ai.getWebsiteEditProgress.useQuery(undefined, {
		enabled: isEnabled && isGenerating,
		refetchInterval: isEnabled && isGenerating ? 1000 : false,
	})

	const editCost = aiGenerationCreditCosts["website-edit"]
	const hasEnoughCredits = platform.aiCreditBalance >= editCost
	const canSend =
		hasEnoughCredits &&
		!hasUnsavedChanges &&
		pendingTurnId === null &&
		undecidedTurn === null &&
		!sendEdit.isPending

	const send = useCallback(() => {
		const trimmedMessage = message.trim()
		if (!canSend || trimmedMessage.length === 0) return

		utilities.ai.getWebsiteEditProgress.setData(undefined, null)
		setMessage("")
		sendEdit.mutate({ message: trimmedMessage })
	}, [canSend, message, sendEdit, utilities])

	const apply = useCallback(
		async (requestId: number) => {
			if (hasUnsavedChanges) {
				const dialog = await alert.confirm(
					"Apply changes",
					"You have unsaved section edits. Applying reloads the page from your website and discards them.",
					{ isDestructive: false },
				)
				if (!dialog.result) return
				dialog.close()
			}

			applyEdit.mutate(requestId)
		},
		[alert, applyEdit, hasUnsavedChanges],
	)

	const discard = useCallback(
		async (requestId: number) => {
			const dialog = await alert.confirm(
				"Discard changes",
				"Are you sure you want to discard the proposed changes? They cannot be recovered.",
			)
			if (!dialog.result) return

			try {
				await discardEdit.mutateAsync(requestId)
			} catch (error) {
				console.error(error)
				dialog.close()
				return Promise.reject(error)
			}
			dialog.close()
		},
		[alert, discardEdit],
	)

	// Refetching runs the query with the placeholder id it was disabled on, which the API rejects, so
	// there is nothing to refetch until a proposal is waiting for a decision.
	const refetchPreview = useCallback(async () => {
		if (undecidedTurn === null) return
		await previewQuery.refetch()
	}, [previewQuery, undecidedTurn])

	const setPreviewVisible = useCallback(
		(isVisible: boolean) => setDismissedPreviewTurnId(isVisible ? null : (undecidedTurn?.id ?? null)),
		[undecidedTurn],
	)

	return {
		message,
		setMessage,
		turns: edits.data ?? [],
		progress: editProgress.data ?? null,
		sendingMessage: sendEdit.isPending ? (sendEdit.variables?.message ?? null) : null,
		undecidedTurn,
		editCost,
		hasEnoughCredits,
		hasUnsavedChanges,
		canSend,
		isSending: sendEdit.isPending,
		applyingTurnId: applyEdit.isPending ? applyEdit.variables : null,
		discardingTurnId: discardEdit.isPending ? discardEdit.variables : null,
		preview,
		// The proposal resolves against the saved site, so saving the page changes what it would do.
		refetchPreview,
		isPreviewVisible: preview !== null && dismissedPreviewTurnId !== undecidedTurn?.id,
		setPreviewVisible,
		send,
		apply,
		discard,
	}
}
